<template>
  <div class="gmf-poll">
    <div v-if="poll.title" class="gmf-subheader">{{ poll.title }}</div>
    <div class="gmf-poll-options" :class="{ 'gmf-poll-options-contained': poll.options.length >= COMPACT_POLL_LIMIT}" :style="pollTypeStyling">
      <div v-for="option of orderedOptions" :key="option.uuid" class="gmf-poll-option" :class="{ 'gmf-active': votedOptions.includes(option.uuid) }" @click.capture="ev => votePoll(option.uuid, ev)">
        <img class="gmf-poll-option-image" v-if="option.imageUrl" :src="option.imageUrl" /> 
        <div class="gmf-poll-option-data">
          <div class="gmf-poll-option-fill" :style="fillStyling(option)"></div>
          <div class="gmf-poll-option-content">
            <div class="gmf-poll-option-method">
              <RadioButton v-if="! poll.isMultipleChoice" :value="votedOptions.includes(option.uuid)" :modelValue="true" :disabled="! isLoggedIn" />
              <Checkbox v-else :modelValue="votedOptions.includes(option.uuid)" :disabled="! isLoggedIn" />
            </div>
            <span class="gmf-poll-option-text">{{ showTranslate && option.translation ? option.translation.text : option.text }}</span>
            <span style="opacity: 0">100%</span> <!-- dummy -->
            <span class="gmf-poll-percentage" :style="percentageStyling(option)"></span>
          </div>
        </div>
      </div>
    </div>
    <span class="gmf-text-small" v-if="totalVotes">{{ totalVotes }} {{ tp('votes') }}</span>
  </div>
</template>

<script>
import state from '@/state';
import tooltip from '@/utils/tooltip';
import callApi from '@/utils/callApi';

import Checkbox from '@/components/Checkbox';
import RadioButton from '@/components/input/RadioButton';

const tp = state.t.prefix('polls');
const COMPACT_POLL_LIMIT = 5;

export default {
  props: {
    poll: {
      Type: Object,
      default: () => null,
    },
    showTranslate: {
      Type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      COMPACT_POLL_LIMIT,
      votedOptions: [...this.poll.observerVotes || []],
    };
  },
  components: {
    Checkbox,
    RadioButton,
  },
  computed: {
    showStats () {
      return ! this.isLoggedIn || this.votedOptions.length > 0;
    },
    totalVotes () {
      return Math.max(this.poll.totalVotes - (this.poll.observerVotes?.length || 0) + this.votedOptions.length, 0);
    },
    orderedOptions () {
      return this.poll.options.toSorted((a,b) => a.order - b.order);
    },
    radioType () {
      return this.poll.isMultipleChoice ? 'checkbox' : 'radio';
    },
    pollTypeStyling () {
      if (this.poll.options.some(o => o.imageUrl) && this.poll.options.length < COMPACT_POLL_LIMIT) {
        return { 'grid-template-columns': '1fr 1fr' };
      } else {
        return { 'grid-template-columns': 'repeat(auto-fit, minmax(220px, 1fr))' }
      }
    },
    isLoggedIn () {
      return state.isLoggedIn();
    }
  },
  methods: {
    tp,
    async votePoll (optionId, ev) {
      if (! this.isLoggedIn) {
        tooltip.create({ x: ev.pageX, y: ev.pageY }, this.tp('log_in_to_vote'), 750);
        return;
      }

      if (this.votedOptions.includes(optionId)) {
        this.votedOptions.splice(this.votedOptions.findIndex(o => o == optionId), 1);
      } else {
        if (this.poll.isMultipleChoice) {
          this.votedOptions.push(optionId);
        } else {
          this.votedOptions = [ optionId ];
        }
      }

      await callApi('POST', `/v2/poll-votes/${this.poll.uuid}`, { options: this.votedOptions });
    },
    percentage(option) {
      if (! this.showStats) {
        return 0;
      }

      let optionVotes = option.votes || 0;
      if (this.poll.observerVotes?.includes(option.uuid)) {
        optionVotes--;
      }
      if (this.votedOptions.includes(option.uuid)) {
        optionVotes++; 
      }
      return Math.floor((optionVotes / (this.totalVotes || 1)) * 100);
    },
    percentageStyling (option) {
      return {
        '--gmf-poll-percentage': this.percentage(option),
        'opacity': ~~this.showStats,
        'position': 'absolute',
        'right': 'var(--gmf-p-3)'
      }
    },
    fillStyling (option) {
      return { 
        'width': this.percentage(option) + '%',
        'opacity': ~~this.showStats,
      };
    },
  }
};
</script>

<style>
.gmf-poll-options {
  display: grid;
  gap: var(--gmf-p-3);
  margin: var(--gmf-p-3) 0;
}

.gmf-poll-option {
  display: flex;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  flex-direction: column;
  border: 2px solid transparent;
  justify-content: space-between;
  border-radius: var(--gmf-rounding);
  background: var(--gmf-poll-col-background);
  transition: border 0.2s ease-in-out;
}
.gmf-poll-option:has(input:focus) {
  border-color: var(--gmf-col-focus);
}

.gmf-poll-options-contained .gmf-poll-option {
  flex-direction: row;
}
.gmf-poll-options-contained .gmf-poll-option-image {
  aspect-ratio: unset;
  width: 90px;
  object-fit: cover;
}

.gmf-poll-option .gmf-radio-btn {
  padding: 0;
  margin: 0;
}

.gmf-poll-option .gmf-radio-btn input[type="radio"] {
  background: var(--gmf-col-white) !important;
}

.gmf-poll-option-fill {
  display: block;
  position: absolute;
  height: 100%;
  top: 0;
  left: 0;
  background: var(--gmf-poll-col-fill-color);
  transition: width 0.8s ease-in-out, opacity 0.1s ease-in-out;
}

.gmf-poll-option-data {
  display: flex;
  flex-grow: 1;
  position: relative;
}

.gmf-poll-option-image {
  z-index: 1;
  width: 100%;
  object-fit: cover;
  aspect-ratio: 1.3;
  user-select: none;
  pointer-events: none;
  background: white;
}

.gmf-poll-option-text {
  flex-grow: 1;
  word-break: break-word;
  font-size: var(--gmf-text-small-font-size);
}

.gmf-poll-option-content {
  padding: var(--gmf-p-3);
  gap: var(--gmf-p-3);
  flex-grow: 1;
  display: flex;
  align-items: center;
  position: relative;
  z-index: 1;
}


.gmf-poll-option-method {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  height: 20px;
}

.gmf-poll-option.gmf-active {
  border: 2px solid var(--gmf-poll-col-border-color);
}

.gmf-poll-percentage {
  counter-reset: gmf-poll-percentage var(--gmf-poll-percentage);
  transition: --gmf-poll-percentage 0.8s ease-in-out, opacity 0.1s ease-in-out;
  opacity: 0;
}

.gmf-poll-percentage::before {
  content: counter(gmf-poll-percentage);
}
.gmf-poll-percentage::after {
  content: '%';
}

@property --gmf-poll-percentage {
  syntax: '<integer>';
  initial-value: 0;
  inherits: false;
}
</style>
